"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const queryString = require("query-string");
const _ = require("lodash");
const ValueObjects_1 = require("./filter/ValueObjects");
const filterKey = (filterId) => `filter-${filterId}`;
/** Helper function to deal with the result of queryString.parse */
function queryStringGet(qs, field) {
    const ret = qs[field];
    // For when the parameter was there but without an equal sign, eg ?a=b&c&d
    if (ret === null)
        return '';
    if (typeof ret === "object")
        return ret[0];
    return ret;
}
class StateSerializer {
    constructor(state, categoryFilter, archiveFilter, fieldFilters, defaultCategory, config) {
        this.state = state;
        this.categoryFilter = categoryFilter;
        this.archiveFilter = archiveFilter;
        this.fieldFilters = fieldFilters;
        this.defaultCategory = defaultCategory;
        this.config = config;
    }
    serialize() {
        const params = new Map();
        if (this.state.keywords.length > 0) {
            params.set('keywords', this.state.keywords);
        }
        if (this.config.resultsDisplay == "paged" && this.state.currentPage > 0) {
            params.set('page', (this.state.currentPage + 1).toString());
        }
        const filterSelections = this.state.filterSelections.serialize();
        for (const filterId of Object.keys(filterSelections)) {
            const selected = filterSelections[filterId][0]; // We only support selecting one filter right now.
            if (!this.excludeFilterValueFromSerialization(filterId, selected)) {
                params.set(filterKey(filterId), selected);
            }
        }
        // If the "All" meta-category is selected, but it is not the default category (because the default is explicitly
        // set to a different category, or implicitly set to "My Preferences", then we need to explicitly encode
        // "all" as the selected category.
        if (this.categoryFilter !== null) {
            if ((!filterSelections.hasOwnProperty(this.categoryFilter.id) ||
                filterSelections[this.categoryFilter.id].length == 0) &&
                this.defaultCategory != null) {
                params.set(filterKey(this.categoryFilter.id), "all");
            }
        }
        const serialized = queryString.stringify(this.mapToObj(params));
        return serialized.length == 0 ? '' : '?' + serialized;
    }
    /**
     * If a value represents the default value that would be displayed in the UI, regardless of whether we explicitly
     * put it in the URL or not, then don't bother putting it in the URL because it is just clutter.
     */
    excludeFilterValueFromSerialization(filterId, filterValue) {
        return ((this.defaultCategory !== null &&
            this.categoryFilter !== null &&
            filterId == this.categoryFilter.id &&
            filterValue == this.defaultCategory.getId()) ||
            (this.archiveFilter !== null &&
                filterId == this.archiveFilter.id &&
                filterValue == ValueObjects_1.ArchivedStatus.CURRENT.getId()));
    }
    mapToObj(map) {
        return Array.from(map.entries()).reduce((obj, entry) => {
            obj[entry[0]] = entry[1];
            return obj;
        }, {});
    }
    /**
     * Deserialize and compare to the state which was given to the constructor. If different, then return a new
     * state object, which is a shallow copy of the existing state, with the newly deserialized values assigned to it.
     * If it is the same, the original state object is returned.
     */
    deserialize(location) {
        const oldUrlState = new StateSerializer(this.state, this.categoryFilter, this.archiveFilter, this.fieldFilters, this.defaultCategory, this.config).serialize();
        if (oldUrlState == location.search) {
            return this.state;
        }
        const query = queryString.parse(location.search);
        const newState = _.clone(this.state);
        newState.keywords = query.hasOwnProperty('keywords') ? queryStringGet(query, 'keywords') : '';
        newState.currentPage = this.getCurrentPage(query);
        this.setFieldFilterSelections(newState.filterSelections, query);
        this.setCategoryFilterSelection(newState.filterSelections, query);
        this.setArchiveFilterSelection(newState.filterSelections, query);
        return newState;
    }
    getCurrentPage(query) {
        return this.config.resultsDisplay == "infiniteScrolling" || !query.hasOwnProperty('page')
            ? 0
            : Math.max(0, parseInt(queryStringGet(query, 'page')) - 1);
    }
    setFieldFilterSelections(filterSelections, query) {
        return this.fieldFilters.forEach((filter) => {
            const key = filterKey(filter.id);
            if (!query.hasOwnProperty(key)) {
                filterSelections.clearSelected(filter);
            }
            else {
                filterSelections.setSelected(filter, this.findFilterValueInQuery(filter, query), true);
            }
        });
    }
    /**
     * Look at the query to find out which category to select.
     *  + If explicitly set to a particular category ID, use that.
     *  + If explicitly set to "all" or the default category is null, then leave it blank.
     *  + Otherwise use the default category.
     */
    setCategoryFilterSelection(filterSelections, query) {
        if (this.categoryFilter === null) {
            return;
        }
        const value = this.findFilterValueInQuery(this.categoryFilter, query);
        const queryKey = filterKey(this.categoryFilter.id);
        if (value == null) {
            if (this.defaultCategory != null && (!query.hasOwnProperty(queryKey) || queryStringGet(query, queryKey) != "all")) {
                filterSelections.setSelected(this.categoryFilter, this.defaultCategory, true);
            }
            else {
                filterSelections.clearSelected(this.categoryFilter);
            }
        }
        else {
            filterSelections.setSelected(this.categoryFilter, value, true);
        }
    }
    /**
     * Look at the query to find out which archived status to use. If blank, default to "Current".
     */
    setArchiveFilterSelection(filterSelections, query) {
        if (this.archiveFilter !== null) {
            const value = this.findFilterValueInQuery(this.archiveFilter, query);
            filterSelections.setSelected(this.archiveFilter, value != null ? value : ValueObjects_1.ArchivedStatus.CURRENT, true);
        }
    }
    findFilterValueInQuery(filter, query) {
        return filter.values.find((value) => value.getId() == queryStringGet(query, filterKey(filter.id)));
    }
}
exports.StateSerializer = StateSerializer;
//# sourceMappingURL=state serializer.js.map


// WEBPACK FOOTER //
// ./build/news2/advancedSearch/state serializer.js